import React, { createContext, useState, useEffect, useContext } from 'react';

// Create a Context for the cart
const CartContext = createContext();

// Custom hook to use the CartContext
export const useCart = () => {
    return useContext(CartContext);
};

// CartProvider component to manage cart functionality
export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState([]);
    const [total, setTotal] = useState(0); // To store cart total

    // Initialize cart from localStorage if available
    useEffect(() => {
        const storedCart = JSON.parse(localStorage.getItem('cart')) || [];
        setCart(storedCart);
    }, []);

    // Save the cart to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cart));
        calculateTotal();
    }, [cart]);

    // Add a course to the cart
    const addToCart = (course, quantity = 1) => {
        setCart((prevCart) => {
            const courseIndex = prevCart.findIndex((item) => item.id === course.id);

            if (courseIndex !== -1) {
                // If course already exists, update the quantity
                const updatedCart = [...prevCart];
                updatedCart[courseIndex].quantity += quantity;
                return updatedCart;
            }

            return [...prevCart, { ...course, quantity }];
        });
    };

    // Remove a course from the cart
    const removeFromCart = (courseId) => {
        setCart((prevCart) => prevCart.filter((item) => item.id !== courseId));
    };

    // Update course quantity in the cart
    const updateQuantity = (courseId, quantity) => {
        setCart((prevCart) => {
            return prevCart.map((item) =>
                item.id === courseId
                    ? { ...item, quantity }
                    : item
            );
        });
    };

    // Calculate the total price of the cart
    const calculateTotal = () => {
        const subtotal = cart.reduce((total, item) => total + item.price * item.quantity, 0);
        setTotal(subtotal);
    };

    // Empty the cart
    const emptyCart = () => {
        setCart([]);
    };

    return (
        <CartContext.Provider
            value={{
                cart,
                total,
                addToCart,
                removeFromCart,
                updateQuantity,
                emptyCart
            }}
        >
            {children}
        </CartContext.Provider>
    );
};
